import * as yup from 'yup';

import { BaseYup, MAX_INPUT, MAX_LENGTH_300 } from '@/config/baseYup';
import i18n from '@/config/i18n';
import { MAX_LENGTH } from '../constants';
import { giftQuantityType } from '../constants/gift';

export const giftSearchFormSchema = BaseYup.object({
  keyword: BaseYup.string().max(MAX_INPUT),
  status: BaseYup.string().max(MAX_INPUT).nullable(),
  start_date: BaseYup.date().nullable(),
  end_date: BaseYup.date().nullable(),
});

export const giftFormSchema = BaseYup.object({
  name: BaseYup.string().label(i18n.t('gift.name')).required().max(MAX_INPUT),
  gift_type_id: BaseYup.number()
    .required(i18n.t('gift.required.gift_type_id'))
    .transform((value) => (Number.isNaN(value) ? null : value)),
  start_date: BaseYup.date().nullable().label(i18n.t('gift.start_date')),
  end_date: BaseYup.date().nullable().label(i18n.t('gift.end_date')),
  description: BaseYup.string()
    .label(i18n.t('gift.detail'))
    .nullable()
    .max(MAX_LENGTH_300),
  points_required_for_student: BaseYup.string()
    .required(i18n.t('gift.required.points_required_for_student'))
    .transform((value) => value.replace(/,/g, '')),
  points_required_for_teacher: BaseYup.string()
    .required(i18n.t('gift.required.points_required_for_teacher'))
    .transform((value) => value.replace(/,/g, '')),
  gift_quantity_type: BaseYup.string().label(i18n.t('gift.limit')).required(),
  total_quantity: BaseYup.string()
    .max(MAX_LENGTH.GIFT_POINT)
    .transform((value) => value.replace(/,/g, ''))
    .when('gift_quantity_type', {
      is: (val: string) => val !== giftQuantityType.noLimit,
      then: (schema) => schema.required(i18n.t('gift.required.total_quantity')),
      otherwise: (schema) => schema.notRequired(),
    }),
  email_content: BaseYup.string()
    .required(i18n.t('gift.required.email_content'))
    .max(MAX_LENGTH.GIFT_EMAIL_CONTENT),
  videos: BaseYup.array()
    .of(
      BaseYup.object().shape({
        name: BaseYup.string()
          .max(255)
          .test(
            'name-required-if-thumbnail-path-exists',
            i18n.t('gift.error.name_required'),
            function (value) {
              const { thumbnail_path } = this.parent;
              return !thumbnail_path || (thumbnail_path && value);
            },
          ),
        thumbnail_id: BaseYup.number().nullable(),
        duration: BaseYup.number().nullable(),
        video_id: BaseYup.number().nullable(),
        thumbnail_path: BaseYup.string()
          .nullable()
          .test(
            "thumbnail-path-required-if-name-exists",
            i18n.t("gift.error.thumbnail_path_required"),
            function (value) {
              const { name } = this.parent;
              return !name || (name && value);
            }
          ),
        new_thumbnail_id: BaseYup.number().nullable(),
        new_video_id: BaseYup.number().nullable(),
        old_thumbnail_id: BaseYup.number().nullable(),
        old_video_id: BaseYup.number().nullable(),
      }),
    )
    .max(5, i18n.t('gift.error.max_videos')),
});

export const giftTypeFormSchema = BaseYup.object({
  gift_type_name: BaseYup.string()
    .label(i18n.t('gift.gift_type_dialog.name'))
    .required()
    .max(MAX_INPUT),
});

export type GiftFormSearchType = yup.InferType<typeof giftSearchFormSchema>;
export type GiftFormType = yup.InferType<typeof giftFormSchema>;
export type GiftTypeFormType = yup.InferType<typeof giftTypeFormSchema>;
